export type ErrorCode =
  | "no-source"
  | "no-folder"
  | "permission"
  | "camera"
  | "microphone"
  | "unsupported"
  | "encoder"
  | "write"
  | "interrupted"
  | "internal";

/** Every failure in this app carries a code and a sentence a user can act on. */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly detail: string | null;

  constructor(code: ErrorCode, message: string, detail?: string) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.detail = detail ?? null;
  }
}

export function toAppError(error: unknown, code: ErrorCode = "internal"): AppError {
  if (error instanceof AppError) return error;
  const detail = error instanceof Error ? error.message : String(error);
  return new AppError(code, sentenceFor(code), detail);
}

export function sentenceFor(code: ErrorCode): string {
  switch (code) {
    case "no-source":
      return "Add a screen or turn the camera on first.";
    case "no-folder":
      return "Choose a folder to save recordings into (Settings).";
    case "permission":
      return "macOS blocked the capture. Allow Screen Recording for this browser in System Settings → Privacy & Security.";
    case "camera":
      return "macOS blocked the camera. Allow Camera for this browser in System Settings → Privacy & Security.";
    case "microphone":
      return "macOS blocked the microphone. Allow Microphone for this browser in System Settings → Privacy & Security.";
    case "unsupported":
      return "This browser can’t record MP4. Chrome 126 or newer is required.";
    case "encoder":
      return "The encoder stopped. The take up to that point was still saved.";
    case "write":
      return "Couldn’t write to disk. The take is safe in the recovery journal.";
    case "interrupted":
      return "The capture ended early (screen share stopped or display disconnected). What was recorded is saved.";
    case "internal":
      return "Something went wrong. What was recorded is saved.";
  }
}

/**
 * Chrome reports a cancelled picker as AbortError. A NotAllowedError is a real
 * denial — the OS or the browser refused — and must not be swallowed as a cancel.
 */
export function isPickerCancel(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
